import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import { useDispatch } from "react-redux";
import Draggable from "react-draggable";
import { setcqlData } from "../../../features/layers/cqlDataSlice";
import { useMap } from "react-leaflet";
import { jsPDF } from "jspdf";
import { FaFilePdf, FaTimes } from "react-icons/fa";

// Import Highcharts
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';

// Initialize Highcharts modules
if (typeof Highcharts === 'object') {
  highchartsMore(Highcharts);
  solidGauge(Highcharts);
}

// Utility function to convert UTC to IST
const convertUTCToIST = (utcHour, dateString) => {
  const [year, month, day] = [
    dateString.substring(0, 4),
    dateString.substring(4, 6),
    dateString.substring(6, 8)
  ];
  
  // Create date object in UTC
  const utcDate = new Date(Date.UTC(year, month - 1, day, utcHour, 0, 0));
  
  // Convert to IST (UTC + 5 hours 30 mins)
  const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
  
  const istHour = istDate.getUTCHours().toString().padStart(2, '0');
  const istMinutes = istDate.getUTCMinutes().toString().padStart(2, '0');

  return {
    istHour: istDate.getUTCHours(),
    istTime: `${istHour}:${istMinutes}`, // Fixed to correctly include the 30 minutes offset
    utcTime: `${utcHour.toString().padStart(2, '0')}:00`,
    fullDate: istDate
  };
};

// Format date for display
const formatDisplayDate = (dateString) => {
  const [year, month, day] = [
    dateString.substring(0, 4),
    dateString.substring(4, 6),
    dateString.substring(6, 8)
  ];
  return new Date(`${year}-${month}-${day}`).toLocaleDateString('en-GB');
};

const TrendChart = ({ position = {}, location, onClose, mainData = {} }) => {
  const dispatch = useDispatch();
  const map = useMap();
  const [loading, setLoading] = useState(true);
  const [locationName, setLocationName] = useState("");
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const exportRef = useRef(null);
  const chartRef = useRef(null);
  const [trendData, setTrendData] = useState([]);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // New state to track if location is fully loaded
  const [locationLoaded, setLocationLoaded] = useState(false);

  // State for date info
  const [currentDate, setCurrentDate] = useState("");
  const [dataDate, setDataDate] = useState("");

  // CRITICAL: Use refs to track mounts and prevent loops
  const isMounted = useRef(true);
  const hasFetchedData = useRef(false);
  const requestController = useRef(null);
  const locationNameController = useRef(null);

  // Track window dimensions - runs ONCE
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Disable map scroll - runs ONCE
  useEffect(() => {
    if (map) {
      map.scrollWheelZoom.disable();
    }

    return () => {
      if (map) {
        map.scrollWheelZoom.enable();
      }
    };
  }, [map]);

  // Get warning level - Updated based on provided image table ranges
  const getWarningLevel = useCallback((value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 23) return { text: 'Safe', recommendation: 'No recommendation', color: '#32CD32' };
    if (numValue < 25) return { text: 'Caution', recommendation: 'Very heavy work (45/15)', color: '#FFFF00' };
    if (numValue < 28) return { text: 'Warning', recommendation: 'Heavy work (30/15)', color: '#FFA500' };
    if (numValue < 30) return { text: 'Severe', recommendation: 'Moderate work (30/30)', color: '#FF4500' };
    if (numValue <= 33) return { text: 'Danger', recommendation: 'Light work (15/45)', color: '#FF0000' };
    return { text: 'Extreme', recommendation: 'Rest', color: '#000000' };
  }, []);

  // Get location name - memoized
  const fetchLocationName = useCallback(async (lat, lng) => {
    if (locationNameController.current) {
      locationNameController.current.abort();
    }
    
    locationNameController.current = new AbortController();
    
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        { 
          timeout: 5000,
          signal: locationNameController.current.signal 
        }
      );
      if (response.data.display_name && isMounted.current) {
        setLocationName(response.data.display_name);
        setLocationLoaded(true); // Set location loaded to true
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching location name:", error);
        // Even if location fetch fails, set loaded to true to show chart
        if (isMounted.current) {
          setLocationLoaded(true);
        }
      }
    }
  }, []);

  // Fetch WBGT data - memoized
  const fetchTrendData = useCallback(async (lat, lng) => {
    if (requestController.current) {
      requestController.current.abort();
    }
    
    requestController.current = new AbortController();
    
    setLoading(true);
    setError(null);
    
    try {
      const currentDate = new Date();
      const formattedDate = currentDate.toISOString().split('T')[0];
      
      let hour = "07";
      if (mainData && mainData.date) {
        const hourMatch = mainData.date.match(/hr-(\d+)/);
        if (hourMatch) {
          hour = hourMatch[1].padStart(2, '0');
        }
      }
      
      const dateWithHour = `${formattedDate}-hr-${hour}-00-00`;
      const apiUrl = `https://api.ncmrwf.gov.in/wbgttrend/?date=${dateWithHour}&odate=${formattedDate}&lat=${lat}&lon=${lng}`;
      
      const response = await axios.get(apiUrl, { 
        timeout: 25000,
        signal: requestController.current.signal 
      });

      if (response.data && Array.isArray(response.data)) {
        const dataEntry = response.data[0];
        
        if (dataEntry && dataEntry.files && dataEntry.files.length > 0) {
          setDataDate(dataEntry.date);
          
          const formattedData = dataEntry.files.map(file => {
            const hourMatch = file.filename.match(/wbgt_\d+_(\d+)_raw\.tif/);
            const utcHour = hourMatch ? parseInt(hourMatch[1]) : 0;
            const timeInfo = convertUTCToIST(utcHour, dataEntry.date);
            const wbgt = parseFloat(file.wbgt) || 0;
            const warningInfo = getWarningLevel(wbgt);
            
            return {
              hour: utcHour,
              utcHour: utcHour,
              istHour: timeInfo.istHour,
              utcTime: timeInfo.utcTime,
              istTime: timeInfo.istTime,
              wbgt: wbgt,
              level: file.level || warningInfo.text,
              recommendation: warningInfo.recommendation,
              fullDate: timeInfo.fullDate
            };
          }).sort((a, b) => a.hour - b.hour);
        
          setTrendData(formattedData);
        } else {
          setTrendData(generateSampleData(dataEntry?.date || formattedDate.replace(/-/g, '')));
        }
      } else {
        throw new Error("No data received from API");
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      setError(`Failed to fetch trend data: ${error.message}`);
      setTrendData(generateSampleData());
    } finally {
      setLoading(false);
      hasFetchedData.current = true;
    }
  }, [getWarningLevel, mainData]);

  // Generate sample data
  const generateSampleData = useCallback((dateString = null) => {
    const currentDate = dateString || new Date().toISOString().split('T')[0].replace(/-/g, '');
    const hours = [];
    
    for (let utcHour = 0; utcHour < 24; utcHour++) {
      let wbgtValue;
      if (utcHour < 6) wbgtValue = 18 + (Math.random() * 2);
      else if (utcHour < 12) wbgtValue = 20 + (utcHour - 6) * 1.5 + (Math.random() * 2);
      else if (utcHour < 15) wbgtValue = 29 + (Math.random() * 3);
      else if (utcHour < 18) wbgtValue = 26 + (Math.random() * 3);
      else wbgtValue = 20 + (Math.random() * 3);
      
      const timeInfo = convertUTCToIST(utcHour, currentDate);
      const warningInfo = getWarningLevel(wbgtValue);
      
      hours.push({
        hour: utcHour,
        utcHour: utcHour,
        istHour: timeInfo.istHour,
        utcTime: timeInfo.utcTime,
        istTime: timeInfo.istTime,
        wbgt: parseFloat(wbgtValue.toFixed(1)),
        level: warningInfo.text,
        recommendation: warningInfo.recommendation,
        fullDate: timeInfo.fullDate
      });
    }
    
    return hours.sort((a, b) => a.hour - b.hour);
  }, [getWarningLevel]);

  // MAIN INITIALIZATION
  useEffect(() => {
    isMounted.current = true;
    setCurrentDate(new Date().toISOString().split('T')[0].replace(/-/g, ''));
    
    if (!hasFetchedData.current) {
      const coords = position?.location || location;
      const lat = coords?.lat;
      const lng = coords?.lng;
      
      if (lat && lng) {
        fetchTrendData(lat, lng);
        fetchLocationName(lat, lng);
      } else {
        setError("Location data is missing");
        setLoading(false);
        setLocationLoaded(true); // Set loaded even on error
      }
    }
    
    return () => {
      isMounted.current = false;
      hasFetchedData.current = false;
      if (requestController.current) requestController.current.abort();
      if (locationNameController.current) locationNameController.current.abort();
      if (chartRef.current && chartRef.current.chart) {
        try { chartRef.current.chart.destroy(); } catch (e) {}
      }
    };
  }, []);

  const handleRetry = useCallback(() => {
    const coords = position?.location || location;
    const lat = coords?.lat;
    const lng = coords?.lng;
    if (lat && lng) {
      setError(null);
      setLocationLoaded(false); // Reset location loaded state
      fetchTrendData(lat, lng);
      fetchLocationName(lat, lng);
    }
  }, [fetchTrendData, position?.location, location]);

  // Handle PDF download
  const handleDownload = async () => {
    if (!chartRef.current || !chartRef.current.chart) return;

    try {
      const chart = chartRef.current.chart;
      const svg = chart.getSVG();
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      pdf.setFontSize(12);
      pdf.setTextColor(51, 51, 51);
      pdf.text('National Centre for Medium Range Weather Forecasting (NCMRWF)', 105, 10, { align: 'center' });
      pdf.setFontSize(16);
      pdf.text('WBGT Hourly Trend', 105, 20, { align: 'center' });
      pdf.setFontSize(12);
      pdf.text(locationName || '', 105, 27, { align: 'center' });
      
      const displayDate = dataDate ? formatDisplayDate(dataDate) : new Date().toLocaleDateString('en-GB');
      pdf.text(`Date: ${displayDate}`, 105, 33, { align: 'center' });
      pdf.text('Time Zone: UTC (shown) / IST (+5:30)', 105, 39, { align: 'center', fontSize: 10 });

      const imgData = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
      const img = new Image();
      img.src = imgData;

      await new Promise((resolve) => {
        if (img.complete) resolve();
        else {
          img.onload = resolve;
          img.onerror = resolve;
        }
      });

      const pageWidth = pdf.internal.pageSize.getWidth() - 20;
      const imgWidth = pageWidth;
      const imgHeight = 150;
      const xPos = (pdf.internal.pageSize.getWidth() - imgWidth) / 2;

      pdf.addImage(img.src, 'PNG', xPos, 45, imgWidth, imgHeight);

      let yPos = 200;
      pdf.setFontSize(10);
      pdf.text('Recommendation / Work Limits:', 20, yPos);
      
      yPos += 7;
      const levels = [
        { color: '#32CD32', text: '< 23°C: No recommendation (Safe)' },
        { color: '#FFFF00', text: '23-25°C: Very heavy work (45/15)' },
        { color: '#FFA500', text: '25-28°C: Heavy work (30/15)' },
        { color: '#FF4500', text: '28-30°C: Moderate work (30/30)' },
        { color: '#FF0000', text: '30-33°C: Light work (15/45)' },
        { color: '#000000', text: '> 33°C: Rest' }
      ];
      
      levels.forEach((level, index) => {
        pdf.setFillColor(level.color);
        pdf.rect(20, yPos + (index * 6), 4, 4, 'F');
        pdf.setTextColor(0, 0, 0);
        pdf.text(level.text, 27, yPos + (index * 6) + 3);
      });

      yPos += 45;
      pdf.setFontSize(10);
      pdf.text('Hourly WBGT Data (UTC / IST):', 20, yPos);
      
      yPos += 7;
      pdf.setFontSize(8);
      
      // Adjusted column placement to accommodate "Recommendation"
      pdf.text('UTC Time', 15, yPos);
      pdf.text('IST Time', 35, yPos);
      pdf.text('WBGT (°C)', 55, yPos);
      pdf.text('Level', 75, yPos);
      pdf.text('Recommendation', 95, yPos);
      
      yPos += 5;
      pdf.line(15, yPos, 195, yPos);
      
      trendData.slice(0, 12).forEach((item, index) => {
        yPos += 6;
        pdf.text(item.utcTime, 15, yPos);
        pdf.text(item.istTime, 35, yPos);
        pdf.text(item.wbgt.toFixed(1), 55, yPos);
        pdf.text(item.level, 75, yPos);
        pdf.text(item.recommendation, 95, yPos);
      });

      pdf.save(`WBGT_Hourly_Trend_${locationName ? locationName.replace(/[^a-z0-9]/gi, '_') : 'location'}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setcqlData({ show: false }));
    onClose();
  };

  const handleContainerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        handleClose(e);
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const chartData = React.useMemo(() => {
    return trendData.map(item => ({
      x: item.utcHour,
      y: item.wbgt,
      color: getWarningLevel(item.wbgt).color,
      level: item.level,
      recommendation: item.recommendation,
      utcTime: item.utcTime,
      istTime: item.istTime
    }));
  }, [trendData, getWarningLevel]);

  const chartOptions = React.useMemo(() => {
    const displayDate = dataDate ? formatDisplayDate(dataDate) : new Date().toLocaleDateString('en-GB');
    
    return {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        height: 300,
        spacing: [10, 10, 10, 10]
      },
      title: {
        text: 'WBGT Hourly Trend',
        style: { fontSize: '14px', fontWeight: 'bold', color: '#333' }
      },
      subtitle: {
        text: `Date: ${displayDate} | UTC Time (shown) | IST = UTC + 5:30`,
        style: { fontSize: '10px', color: '#666' }
      },
      xAxis: {
        type: 'category',
        title: { text: 'Time (UTC Hours)', style: { color: '#666', fontSize: '12px' } },
        labels: {
          style: { color: '#666', fontSize: '11px' },
          formatter: function() {
            const utcHour = this.value;
            const dataPoint = trendData.find(item => item.utcHour === utcHour);
            if (dataPoint) {
              return `${utcHour}:00<br/><span style="color: #888; font-size: 9px;">IST: ${dataPoint.istTime}</span>`;
            }
            return `${utcHour}:00`;
          },
          useHTML: true
        },
        tickInterval: 3,
        gridLineWidth: 1,
        gridLineColor: '#f0f0f0'
      },
      yAxis: {
        title: { text: 'WBGT (°C)', style: { color: '#666', fontSize: '12px' } },
        labels: { style: { color: '#666', fontSize: '11px' } },
        min: 20, // Changed from 15 to 20
        max: 40,
        plotBands: [
          { from: 15, to: 23, color: 'rgba(50, 205, 50, 0.1)', label: { text: 'Safe', style: { color: '#32CD32', fontSize: '10px' } } },
          { from: 23, to: 25, color: 'rgba(255, 255, 0, 0.1)', label: { text: 'Caution', style: { color: '#FFFF00', fontSize: '10px' } } },
          { from: 25, to: 28, color: 'rgba(255, 165, 0, 0.1)', label: { text: 'Warning', style: { color: '#FFA500', fontSize: '10px' } } },
          { from: 28, to: 30, color: 'rgba(255, 69, 0, 0.1)', label: { text: 'Severe', style: { color: '#FF4500', fontSize: '10px' } } },
          { from: 30, to: 33, color: 'rgba(255, 0, 0, 0.1)', label: { text: 'Danger', style: { color: '#FF0000', fontSize: '10px' } } },
          { from: 33, to: 40, color: 'rgba(0, 0, 0, 0.1)', label: { text: 'Extreme', style: { color: '#000000', fontSize: '10px' } } }
        ],
        gridLineWidth: 1,
        gridLineColor: '#f0f0f0'
      },
      series: [{
        name: 'WBGT',
        data: chartData,
        lineWidth: 3,
        marker: { enabled: true, radius: 4, symbol: 'circle' }
      }],
      tooltip: {
        shared: false,
        crosshairs: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderRadius: 4,
        style: { color: '#333', fontSize: '12px' },
        formatter: function() {
          const utcTime = `${this.x}:00`;
          const dataPoint = trendData.find(item => item.utcHour === this.x);
          const istTime = dataPoint?.istTime || 'N/A';
          const recommendation = dataPoint?.recommendation || 'N/A';
          const value = this.y.toFixed(1);
          const level = this.point.level;
          return `
            <b>UTC Time: ${utcTime}</b><br/>
            <b>IST Time: ${istTime}</b><br/>
            WBGT: <b>${value} °C</b><br/>
            Status: ${level}<br/>
            Recommendation: <b>${recommendation}</b>
          `;
        }
      },
      legend: { enabled: false },
      credits: { enabled: false },
      plotOptions: {
        series: { cursor: 'pointer', states: { hover: { halo: { size: 5, opacity: 0.25 } } } }
      }
    };
  }, [chartData, dataDate, trendData]);

  const displayDateInfo = React.useMemo(() => {
    const displayDate = dataDate ? formatDisplayDate(dataDate) : new Date().toLocaleDateString('en-GB');
    return `Date: ${displayDate} | UTC (shown) / IST (+5:30)`;
  }, [dataDate]);

  // Determine if we should show loading state
  const showLoading = loading || !locationLoaded;

  return (
    <>
      {showLoading && !error && (
        <LoaderModal>
          <div className="modal-content">
            <i className="fa fa-cog fa-spin" style={{ marginRight: "10px" }}></i>
            {!locationLoaded && loading ? "Loading location and data..." : 
             !locationLoaded ? "Loading location..." : 
             loading ? "Loading WBGT data..." : "Processing..."}
            {dataDate && (
              <div style={{ fontSize: '12px', marginTop: '5px', color: '#666' }}>
                Date: {formatDisplayDate(dataDate)}
              </div>
            )}
          </div>
        </LoaderModal>
      )}

      {error && (
        <ErrorModal>
          <div className="modal-content">
            <i className="fa fa-exclamation-triangle" style={{ marginRight: "10px", color: "#ff3333" }}></i>
            {error}
            {dataDate && (
              <div style={{ fontSize: '12px', marginTop: '5px', color: '#666' }}>
                Date: {formatDisplayDate(dataDate)}
              </div>
            )}
            <div className="button-group">
              <button onClick={handleRetry} className="retry-button">Retry</button>
              <button onClick={handleClose} className="close-button">Close</button>
            </div>
          </div>
        </ErrorModal>
      )}

      {!showLoading && !error && (
        <Draggable 
          handle=".headers" 
          bounds="parent"
          disabled={dimensions.width <= 768}
        >
          <DiagramContainer
            ref={containerRef}
            onClick={handleContainerClick}
            onMouseDown={handleContainerClick}
            dimensions={dimensions}
          >
            <div className="headers">
              <div className="header-content">
                <div className="header-text">
                  <p className="title">WBGT Hourly Trend</p>
                  <p className="location">{locationName || 'Loading location...'}</p>
                  <p className="date-info">{displayDateInfo}</p>
                </div>
                <div className="header-actions">
                  <button 
                    className="download-btn" 
                    onClick={handleDownload}
                    title="Download as PDF"
                    aria-label="Download PDF"
                  >
                    <FaFilePdf className="pdf-icon" />
                  </button>
                  <button 
                    className="close" 
                    onClick={handleClose} 
                    title="Close"
                    aria-label="Close diagram"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            </div>

            <div
              className="chart-scroll-container"
              ref={exportRef}
              onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <div className="chart-container">
                <HighchartsReact
                  highcharts={Highcharts}
                  options={chartOptions}
                  ref={chartRef}
                />
                
                <div className="timezone-info">
                  <div className="timezone-badge utc">
                    <span className="timezone-label">UTC Time</span>
                  </div>
                  <div className="timezone-badge ist">
                    <span className="timezone-label">IST Time (UTC + 5:30)</span>
                  </div>
                </div>
                
                <div className="warning-levels">
                  <h4>WBGT Warning Levels & Recommendations:</h4>
                  <div className="levels-grid">
                    <div className="level-item">
                      <span className="level-color" style={{ backgroundColor: '#32CD32' }}></span>
                      <span className="level-text">&lt; 23°C: No recommendation</span>
                    </div>
                    <div className="level-item">
                      <span className="level-color" style={{ backgroundColor: '#FFFF00' }}></span>
                      <span className="level-text">23-25°C: Very heavy work (45/15)</span>
                    </div>
                    <div className="level-item">
                      <span className="level-color" style={{ backgroundColor: '#FFA500' }}></span>
                      <span className="level-text">25-28°C: Heavy work (30/15)</span>
                    </div>
                    <div className="level-item">
                      <span className="level-color" style={{ backgroundColor: '#FF4500' }}></span>
                      <span className="level-text">28-30°C: Moderate work (30/30)</span>
                    </div>
                    <div className="level-item">
                      <span className="level-color" style={{ backgroundColor: '#FF0000' }}></span>
                      <span className="level-text">30-33°C: Light work (15/45)</span>
                    </div>
                    <div className="level-item">
                      <span className="level-color" style={{ backgroundColor: '#000000' }}></span>
                      <span className="level-text">&gt; 33°C: Rest</span>
                    </div>
                  </div>
                  
                  <div className="data-table">
                    <h5>Hourly Data (UTC / IST):</h5>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>UTC Time</th>
                            <th>IST Time</th>
                            <th>WBGT (°C)</th>
                            <th>Level</th>
                            <th>Recommendation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trendData.slice(0, 8).map((item, index) => (
                            <tr key={index}>
                              <td>{item.utcTime}</td>
                              <td>{item.istTime}</td>
                              <td>{item.wbgt.toFixed(1)}</td>
                              <td>
                                <span className="level-badge" style={{ 
                                  backgroundColor: getWarningLevel(item.wbgt).color,
                                  color: item.wbgt >= 30 ? '#fff' : '#000',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  fontSize: '10px'
                                }}>
                                  {item.level}
                                </span>
                              </td>
                              <td style={{ fontWeight: 500 }}>{item.recommendation}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DiagramContainer>
        </Draggable>
      )}
    </>
  );
};

export default TrendChart;

// Styled components - updated with timezone info
const DiagramContainer = styled.div`
  position: absolute;
  background: #fff;
  z-index: 99999;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0,0,0,0.3);
  overflow: hidden;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  
  ${({ dimensions }) => {
    if (dimensions.width > 1200) {
      return `
        width: 800px;
        height: 680px;
        left: calc(50% - 400px);
        top: 50px;
      `;
    } else if (dimensions.width > 992) {
      return `
        width: 750px;
        height: 630px;
        left: calc(50% - 375px);
        top: 40px;
      `;
    } else if (dimensions.width > 768) {
      return `
        width: 700px;
        height: 580px;
        left: calc(50% - 350px);
        top: 30px;
      `;
    } else if (dimensions.width > 576) {
      return `
        width: 95vw;
        height: 85vh;
        left: 2.5vw;
        top: 7.5vh;
      `;
    } else {
      return `
        width: 98vw;
        height: 90vh;
        left: 1vw;
        top: 5vh;
      `;
    }
  }}

  .headers {
    padding: ${({ dimensions }) => dimensions.width > 768 ? '12px 15px' : '10px 12px'};
    cursor: ${({ dimensions }) => dimensions.width > 768 ? 'move' : 'default'};
    border-bottom: 1px solid #eee;
    user-select: none;
    background: #f8f9fa;
    
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
    }
    
    .header-text {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }
    
    .title {
      font-weight: bold;
      color: #333;
      margin: 0;
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '18px' : 
        dimensions.width > 768 ? '17px' : '16px'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .location {
      color: #666;
      margin: ${({ dimensions }) => dimensions.width > 768 ? '4px 0 0 0' : '3px 0 0 0'};
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '14px' : 
        dimensions.width > 768 ? '13px' : '12px'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .date-info {
      color: #666;
      margin: ${({ dimensions }) => dimensions.width > 768 ? '4px 0 0 0' : '3px 0 0 0'};
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '12px' : 
        dimensions.width > 768 ? '11px' : '10px'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: ${({ dimensions }) => dimensions.width > 768 ? '10px' : '8px'};
    flex-shrink: 0;
  }
  
  .download-btn {
    background: #3f4854;
    color: white;
    border: none;
    width: ${({ dimensions }) => 
      dimensions.width > 1200 ? '32px' : 
      dimensions.width > 768 ? '30px' : '28px'};
    height: ${({ dimensions }) => 
      dimensions.width > 1200 ? '32px' : 
      dimensions.width > 768 ? '30px' : '28px'};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.2s ease;
    
    &:hover {
      background: #2c343d;
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    .pdf-icon {
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '16px' : 
        dimensions.width > 768 ? '15px' : '14px'};
    }
  }
  
  .close {
    cursor: pointer;
    color: #666;
    background: none;
    border: none;
    padding: 0;
    width: ${({ dimensions }) => 
      dimensions.width > 1200 ? '32px' : 
      dimensions.width > 768 ? '30px' : '28px'};
    height: ${({ dimensions }) => 
      dimensions.width > 1200 ? '32px' : 
      dimensions.width > 768 ? '30px' : '28px'};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
      color: #333;
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    svg {
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '18px' : 
        dimensions.width > 768 ? '17px' : '16px'};
    }
  }
  
  .chart-scroll-container {
    height: calc(100% - ${({ dimensions }) => dimensions.width > 768 ? '70px' : '65px'});
    overflow-y: auto;
    padding: ${({ dimensions }) => dimensions.width > 768 ? '12px' : '8px'};
    background: #f5f5f5;
    
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
    
    .chart-container {
      width: 100%;
      background: white;
      border-radius: 4px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      padding: ${({ dimensions }) => dimensions.width > 768 ? '15px' : '10px'};
      
      .timezone-info {
        display: flex;
        justify-content: center;
        gap: 15px;
        margin: 10px 0 15px 0;
        
        .timezone-badge {
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          
          &.utc {
            background: #e3f2fd;
            color: #1565c0;
            border: 1px solid #bbdefb;
          }
          
          &.ist {
            background: #f3e5f5;
            color: #7b1fa2;
            border: 1px solid #e1bee7;
          }
        }
      }
      
      .warning-levels {
        margin-top: 20px;
        padding-top: 15px;
        border-top: 1px solid #eee;
        
        h4 {
          margin: 0 0 10px 0;
          font-size: ${({ dimensions }) => dimensions.width > 768 ? '14px' : '13px'};
          color: #333;
          font-weight: 600;
        }
        
        h5 {
          margin: 15px 0 8px 0;
          font-size: ${({ dimensions }) => dimensions.width > 768 ? '13px' : '12px'};
          color: #555;
          font-weight: 600;
        }
        
        .levels-grid {
          display: grid;
          grid-template-columns: repeat(${({ dimensions }) => 
            dimensions.width > 768 ? '3' : '2'}, 1fr);
          gap: ${({ dimensions }) => dimensions.width > 768 ? '10px' : '8px'};
          margin-bottom: 15px;
          
          .level-item {
            display: flex;
            align-items: center;
            gap: 8px;
            
            .level-color {
              width: 12px;
              height: 12px;
              border-radius: 2px;
              flex-shrink: 0;
            }
            
            .level-text {
              font-size: ${({ dimensions }) => dimensions.width > 768 ? '12px' : '11px'};
              color: #555;
            }
          }
        }
        
        .data-table {
          .table-container {
            overflow-x: auto;
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: ${({ dimensions }) => dimensions.width > 768 ? '12px' : '11px'};
              
              thead {
                background: #f8f9fa;
                
                th {
                  padding: 8px;
                  text-align: left;
                  border-bottom: 2px solid #dee2e6;
                  color: #495057;
                  font-weight: 600;
                }
              }
              
              tbody {
                tr {
                  border-bottom: 1px solid #dee2e6;
                  
                  &:nth-child(even) {
                    background: #f8f9fa;
                  }
                  
                  td {
                    padding: 8px;
                    color: #212529;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const LoaderModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);

  .modal-content {
    background: white;
    padding: clamp(15px, 5vw, 25px) clamp(20px, 6vw, 30px);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    font-size: clamp(15px, 4vw, 18px);
    font-weight: 500;
    color: #3f4854;
    display: flex;
    align-items: center;
    max-width: 90%;
    width: auto;
    min-width: 200px;
    text-align: center;
    border: 1px solid rgba(0, 0, 0, 0.1);

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 12px;
    }

    .fa-cog {
      font-size: clamp(18px, 5vw, 22px);
      margin-right: clamp(8px, 2vw, 12px);

      @media (max-width: 480px) {
        margin-right: 0;
        margin-bottom: 8px;
      }
    }
  }
`;

const ErrorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);

  .modal-content {
    background: white;
    padding: clamp(15px, 5vw, 25px) clamp(15px, 5vw, 30px);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    font-size: clamp(15px, 4vw, 18px);
    font-weight: 500;
    color: #3f4854;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 90%;
    width: auto;
    min-width: 200px;
    text-align: center;
    border: 1px solid rgba(0, 0, 0, 0.1);

    .fa-exclamation-triangle {
      font-size: clamp(20px, 6vw, 24px);
      margin-bottom: clamp(8px, 2vw, 12px);
    }
  }

  .button-group {
    display: flex;
    gap: clamp(8px, 3vw, 15px);
    margin-top: clamp(12px, 3vw, 20px);
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }

  .retry-button, .close-button {
    padding: clamp(6px, 2vw, 8px) clamp(12px, 4vw, 20px);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: clamp(13px, 3vw, 14px);
    transition: all 0.2s ease;
    min-width: 80px;
    flex: 1;
    max-width: 120px;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    &:active {
      transform: translateY(0);
      box-shadow: none;
    }
  }

  .retry-button {
    background: #3f4854;
    color: white;

    &:hover {
      background: #2c343d;
    }
  }

  .close-button {
    background: #f0f0f0;
    color: #333;

    &:hover {
      background: #e0e0e0;
    }
  }
`;